import { batch, batchObserve, define, reactive } from '@rehaje/reactive'
import { isArray, isPlainObj, Path, toArr } from '@rehaje/shared'

import { FatalAssertReactiveError } from './errors'
import { YArray, YDoc, YUndoManager } from './types'
import {
  defaultOnDocChanged,
  defaultOnReactiveChanged,
  deleteYjs,
  ensurePath,
  initYjsRootMap,
  setYjs,
  spliceYjs,
  yjs2json
} from './utils'

import type {
  CollaboratorOptions,
  CollaboratorPlugin,
  Store,
  YDocListener,
  YEvent,
  YMap,
  YStackItemListener
} from './types'
import type { Disposer } from '@rehaje/shared'

export class Collaborator {
  __r_doc?: YDoc
  options: CollaboratorOptions
  origin = Symbol('__collaborator_origin__')
  /**
   * reactive 响应式根
   */
  reactive?: any
  /**
   * 重做栈长度
   */
  @reactive()
  redoStackLength = 0

  /**
   * yjs 协同根
   */
  root?: YMap

  /**
   * 可选的 Store，可以注入，仅影响报错的方式
   */
  store?: Store

  /**
   * 撤销回退管理引擎
   */
  undoManager?: YUndoManager

  /**
   * 撤销栈长度
   */
  @reactive()
  undoStackLength = 0

  /**
   * yjs 协同文档数据
   */
  get doc(): YDoc {
    if (this.__r_doc?.isDestroyed === false) return this.__r_doc
    this.__r_doc = new YDoc(this.options)
    return this.__r_doc
  }

  get plugins(): CollaboratorPlugin[] {
    return toArr(this.options.plugins)
  }

  constructor(options?: CollaboratorOptions) {
    this.options = { name: 'root', ...options }
    define(this)
  }

  /**
   * 根据是否存在注入的 Store 来决定报错的方式
   */
  __r_raise(error: Error, silent?: boolean) {
    if (typeof this.store?.raise === 'function') {
      this.store.raise(error, silent)
    } else if (silent) {
      console.error(error)
    } else {
      throw error
    }
  }

  /**
   * 是否可重做
   */
  canRedo(): boolean {
    return this.redoStackLength > 0
  }

  /**
   * 是否可撤销
   */
  canUndo(): boolean {
    return this.undoStackLength > 0
  }

  effect(reactive?: any, options?: CollaboratorOptions): Disposer {
    if (!isPlainObj(reactive)) {
      this.__r_raise(new FatalAssertReactiveError())
    }

    options = { ...this.options, ...options }
    this.reactive = reactive
    this.root = this.doc.getMap(options.name)
    this.undoManager = new YUndoManager(this.root, {
      captureTimeout: options.captureTimeout ?? 340,
      trackedOrigins: new Set([this.origin])
    })

    const onDocChanged = options.onDocChanged ?? defaultOnDocChanged
    const onReactiveChanged =
      options.onReactiveChanged ?? defaultOnReactiveChanged
    initYjsRootMap(this.root, this.reactive)

    const dispose = batchObserve(
      this.reactive,
      (events) => {
        if (events.some((event) => event.origins?.includes(this.origin))) return
        options.onDebugEventListener?.('batchObserve', events)
        this.doc.transact(() => {
          for (let i = 0; i < events.length; i++) {
            onReactiveChanged(events[i], i, events, (event) => {
              const yTarget = ensurePath(
                this.root,
                this.reactive,
                event.initialPath
              )
              if (yTarget === undefined) {
                options.onDebugEmptyTarget?.('yjsTarget', event)
                return
              } else if (event.type === 'set') {
                setYjs(yTarget, event.key, event.source.value)
              } else if (event.type === 'delete') {
                deleteYjs(yTarget, event.key)
              } else if (event.type === 'splice' && yTarget instanceof YArray) {
                /**
                 * 这里 items 需要使用 event.source.items 而不是 event.items
                 * 因为需要使用建模后带 id 的精确数据，否则无法撤销重做时精确恢复现场
                 *
                 * 这里 start 和 deleteCount 需要使用 event.start 和 event.deleteCount
                 * 因为需要使用原本未经过自动建模修正的数据，否则 yjs 里的数据会乱掉
                 */
                spliceYjs(
                  yTarget,
                  event.start,
                  event.deleteCount,
                  event.source.items
                )
              }
            })
          }
        }, this.origin)
      },
      { async: false }
    )
    const docListener: YDocListener = (events, transaction) => {
      if (transaction.origin === this.origin) return
      options.onDebugEventListener?.('docListener', events)
      onDocChanged(
        events,
        (events: YEvent[]) => {
          for (const event of events) {
            const rTarget = Path.getIn(this.reactive, event.path)
            if (rTarget === undefined) {
              options.onDebugEmptyTarget?.('reactiveTarget', event)
              continue
            } else if (event.keys?.size) {
              event.keys.forEach((payload, key) => {
                rTarget[key] =
                  payload.action === 'delete'
                    ? undefined
                    : yjs2json(event.target.get(key))
              })
            } else if (event.delta?.length && isArray(rTarget)) {
              let index = 0
              for (const delta of event.delta) {
                if (isArray(delta.insert)) {
                  const items = delta.insert.map((insert) => yjs2json(insert))
                  rTarget.splice(index, 0, ...items)
                  index += delta.insert.length
                }
                if (delta.delete) rTarget.splice(index, delta.delete)
                if (delta.retain) index += delta.retain
              }
            }
          }
        },
        this.origin
      )
    }

    const onStackItemUpdated: YStackItemListener = batch.bound(
      (event, undoManager) => {
        this.undoStackLength = this.undoManager?.undoStack.length ?? 0
        this.redoStackLength = this.undoManager?.redoStack.length ?? 0
        options.onStackUpdated?.(event, undoManager)
      }
    )
    const onStackItemAdded: YStackItemListener = (event, undoManager) => {
      onStackItemUpdated(event, undoManager)
      options.onStackAdded?.(event, undoManager)
    }
    const onStackItemPopped: YStackItemListener = (event, undoManager) => {
      onStackItemUpdated(event, undoManager)
      options.onStackPopped?.(event, undoManager)
    }

    this.root.observeDeep(docListener)
    this.undoManager.on('stack-item-added', onStackItemAdded)
    this.undoManager.on('stack-item-popped', onStackItemPopped)
    this.undoManager.on('stack-item-updated', onStackItemUpdated)

    const disconnects = this.plugins.map((plugin) => {
      const provider = plugin(this)
      provider.connect()
      return () => provider.disconnect()
    })

    return () => {
      dispose()
      this.root?.unobserveDeep(docListener)
      this.undoManager?.off('stack-item-added', onStackItemAdded)
      this.undoManager?.off('stack-item-popped', onStackItemPopped)
      this.undoManager?.off('stack-item-updated', onStackItemUpdated)
      for (const disconnect of disconnects) disconnect()
      this.doc.destroy()
    }
  }

  /**
   * 撤销
   */
  redo(): void {
    this.undoManager?.redo()
  }

  /**
   * 回退
   */
  undo(): void {
    this.undoManager?.undo()
  }
}
